import { selectIllustration } from '@client/model';
import { getQuoteId, isTraditionalLTCQuote } from '@client/util/quoteTypeGuards';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export interface IllustrationMetadata {
  quoteId: string;
  quoteType: 'traditionalLTC' | 'insurance';
  clientId: string;
  fundingSourceId: string;
  selectedPremium: number | undefined;
  requestId: string | null;
  errorDetails?: string[];
  errorDateTime?: string;
  attemptedRetries?: number;
}

interface UseIllustrationMetadataProps {
  sourceQuote: PolicyQuote | TraditionalLTCQuote;
  illustrationError: { errors: string[] } | null;
  client: Client;
  policyApplication: ClientPolicyApplication;
}

export function useIllustrationMetadata({
  sourceQuote,
  illustrationError,
  client,
  policyApplication,
}: UseIllustrationMetadataProps) {
  const illustration = useSelector(selectIllustration);
  const quoteId = useMemo(() => getQuoteId(sourceQuote), [sourceQuote]);

  const buildIllustrationMetadata = useMemo(() => {
    return () => {
      if (!quoteId) return null;

      const isTraditionalLTC = isTraditionalLTCQuote(sourceQuote);
      const activeRequest = illustration.activeRequests?.[quoteId];
      const completedIllustration = illustration.completedIllustrations?.[quoteId];

      // Get requestId from either active request or completed illustration
      const requestId = completedIllustration?.requestId || activeRequest?.requestId || null;

      const metadata: IllustrationMetadata = {
        quoteId,
        quoteType: isTraditionalLTC ? ('traditionalLTC' as const) : ('insurance' as const),
        clientId: client.clientId,
        fundingSourceId: policyApplication.fundingSourceId,
        selectedPremium: policyApplication.illustrationPremiumAmount,
        requestId,
      };

      // Only add error fields if there's an error
      if (illustrationError && illustrationError.errors.length > 0) {
        metadata.errorDetails = illustrationError.errors;
        metadata.errorDateTime = new Date().toISOString();
        metadata.attemptedRetries = activeRequest?.currentPollCount || 0;
      }

      return metadata;
    };
  }, [
    quoteId,
    sourceQuote,
    illustrationError,
    illustration.activeRequests,
    illustration.completedIllustrations,
    client.clientId,
    policyApplication.fundingSourceId,
    policyApplication.illustrationPremiumAmount,
  ]);

  return { buildIllustrationMetadata, quoteId };
}
